import Link from "next/link";
import Head from "next/head";
import React from "react";
import { site } from "@/data/default";

/**
 * Breadcrumb component dengan struktur SEO JSON-LD.
 * Telah diperbaiki agar tidak terjadi slug dobel saat hanya 2 tingkat.
 * Juga diperkuat agar tidak error jika slug tidak terdefinisi.
 */

// Helper untuk merapikan path agar tidak dobel slash
const cleanPath = (base?: string, sub?: string) => {
  const safeBase = base ?? "";
  const safeSub = sub ?? "";
  return `${safeBase.replace(/\/$/, "")}/${safeSub.replace(/^\//, "")}`;
};

// Helper aman untuk slug string
const safeSlug = (slug?: string) => slug ?? "";

const Breadcrumb = ({
  title,
  pages,
  subtitle,
}: {
  title: string;
  pages: { title: string; slug?: string }[]; // ← slug dibuat optional untuk keamanan ekstra
  subtitle?: string;
}) => {
  // Struktur JSON-LD breadcrumb list
  const listItems = [
    {
      name: "Home",
      item: site.url || process.env.NEXT_PUBLIC_SITE_URL || "",
    },
    ...pages.map((page) => ({
      name: page.title,
      item: cleanPath(site.url, safeSlug(page.slug)),
    })),
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: listItems.map((li, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: li.name,
      item: li.item,
    })),
  };

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      </Head>
      <div className="overflow-hidden shadow-breadcrumb pt-[140px] sm:pt-[140px] lg:pt-[160px] xl:pt-[140px]">
        <div className="border-t border-gray-3">
          <div className="max-w-[1170px] w-full mx-auto px-4 sm:px-8 xl:px-0 py-5 xl:py-10">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <h1 className="font-semibold text-dark text-xl sm:text-2xl xl:text-custom-2">
                {title}
                {subtitle && <span className="sr-only"> : {subtitle}</span>}
              </h1>

              <ul className="flex items-center gap-2">
                <li className="text-custom-sm hover:text-purple">
                  <Link href="/" title={site.name}>
                    Home
                  </Link>
                  <span> /</span>
                </li>

                {pages.map((page, key) => {
                  const currentSlug = safeSlug(page.slug);
                  const previousSlug = safeSlug(pages[key - 1]?.slug);

                  const fullSlug =
                    key === 0 ? currentSlug : cleanPath(previousSlug, currentSlug);

                  return (
                    <li
                      key={key}
                      className="text-custom-sm capitalize flex items-center gap-1"
                    >
                      <Link
                        href={fullSlug || "/"} // Fallback aman
                        title={page.title ?? ""}
                        className={
                          key === pages.length - 1
                            ? "text-purple"
                            : "hover:text-purple"
                        }
                      >
                        {page.title ?? title}
                      </Link>
                      {key !== pages.length - 1 && <span> /</span>}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Breadcrumb;
